<script setup lang="ts">
import type { MenuItem } from '@groveco/http-services'
import { SkyButton, SkyIcon, SkyLink } from '@groveco/skylight'
import { WELLNESS_ITEM_ID } from '@/constants'
import { ROUTES } from '~/constants/routes'

type SkyButtonRef = InstanceType<typeof SkyButton> | null

const props = defineProps<MenuItem>()

const expandedChild = ref<MenuItem | null>(null)
const expandedChildRefs = ref<Record<string, SkyButtonRef>>({})
const flyout = ref<HTMLElement | null>(null)
const isActive = ref(false)
const root = ref<HTMLElement | null>(null)

const firstChildWithChildren = computed(() => {
  return props.children.find(({ children }) => children.length)
})

const isSpecialMenuItem = computed(() => Boolean(props.id === WELLNESS_ITEM_ID))

const topLevelHref = computed(() => {
  // An override for the href in the top level nav.
  return isSpecialMenuItem.value ? ROUTES.wellnessRoute.to : null
})

/**
 * Check on focus if the root element (this menu item) contains the active focused element.
 * If not, set isActive to false.
 */
const checkFocusIn = () => {
  isActive.value = root.value?.contains(document.activeElement) || false
  if (!isActive.value) {
    window.removeEventListener('focusin', checkFocusIn)
  }
}

/**
 * Expand a child menu item.
 * When changeFocus is enabled the focus will be moved to the first child of the expanded child.
 */
const expandChild = (child: MenuItem, changeFocus = false) => {
  expandedChild.value = child
  if (changeFocus) {
    nextTick(() => {
      expandedChildRefs.value[child.id]?.$el.focus()
    })
  }
}

/**
 * Open the menu and focus the flyout.
 */
const handleOpenMenu = () => {
  isActive.value = true
  window.addEventListener('focusin', checkFocusIn)
  nextTick(() => {
    flyout.value?.focus()
  })
}

/**
 * If menu list item is active and no child is active expand the first child with children.
 */
watch(isActive, () => {
  if (isActive.value && !expandedChild.value) {
    expandedChild.value = firstChildWithChildren.value || props
  }
})

onUnmounted(() => {
  window.removeEventListener('focusin', checkFocusIn)
})
</script>

<template>
  <li
    ref="root"
    class="HeaderMenuListItem"
    :class="{ HeaderMenuListItem_SpecialTreatment: isSpecialMenuItem }"
    @click="isActive = false"
    @keyup.esc="isActive = false"
    @mouseenter="isActive = true"
    @mouseleave="isActive = false"
  >
    <SkyLink
      data-test-id="menu-link"
      :to="topLevelHref || href"
      class="HeaderMenuListItem_Link"
      secondary
    >
      {{ text }}
    </SkyLink>
    <template v-if="children.length">
      <SkyButton
        :aria-label="`open ${text} menu`"
        data-test-id="menu-open"
        icon
        text
        secondary
        @click.stop="handleOpenMenu"
      >
        <SkyIcon aria-hidden="true" class="SkyButton_Icon" name="dropdown" />
      </SkyButton>
      <div
        v-if="isActive"
        ref="flyout"
        data-test-id="flyout"
        class="HeaderMenuListItem_Flyout"
        tabindex="0"
      >
        <div
          :class="{
            'HeaderMenuListItem_FlyoutContainer--nested':
              firstChildWithChildren,
          }"
          class="HeaderMenuListItem_FlyoutContainer"
        >
          <ul
            v-if="firstChildWithChildren"
            class="HeaderMenuListItem_FlyoutList"
          >
            <li
              v-for="child in children"
              :key="child.id"
              class="HeaderMenuListItem_FlyoutListItem"
            >
              <SkyButton
                v-if="child.children.length"
                :aria-expanded="String(expandedChild === child)"
                data-test-id="expand-child"
                :secondary="expandedChild !== child"
                class="HeaderMenuListItem_FlyoutListItemButton"
                block
                icon
                @click.stop="expandChild(child, true)"
                @mouseenter="expandChild(child)"
              >
                <SkyLink
                  :to="child.href"
                  data-test-id="flyout-link"
                  :secondary="expandedChild !== child"
                >
                  {{ child.text }}
                </SkyLink>
                <SkyIcon aria-hidden="true" name="chevron-right" />
              </SkyButton>
              <SkyLink
                v-else
                :to="child.href"
                data-test-id="flyout-link"
                class="HeaderMenuListItem_Link HeaderMenuListItem_Link--nested"
                secondary
                @mouseenter="expandedChild = null"
              >
                {{ child.text }}
              </SkyLink>
            </li>
            <li class="HeaderMenuListItem_FlyoutListItem">
              <SkyLink
                class="HeaderMenuListItem_FlyoutListItem_Link"
                :to="href"
                data-test-id="flyout-link"
                secondary
              >
                Shop All {{ text }}
                <template #icon>
                  <SkyIcon
                    aria-hidden="true"
                    class="SkyLink_Icon"
                    name="arrow"
                    secondary
                  />
                </template>
              </SkyLink>
            </li>
          </ul>
          <ul v-if="expandedChild" class="HeaderMenuListItem_FlyoutList">
            <li
              v-for="(child, index) in expandedChild.children"
              :key="child.id"
              class="HeaderMenuListItem_FlyoutListItem"
            >
              <SkyLink
                :ref="(el) => { if (index === 0 && expandedChild?.id) { expandedChildRefs[expandedChild.id] = el as SkyButtonRef }}"
                :to="child.href"
                data-test-id="flyout-link"
                secondary
              >
                {{ child.text }}
              </SkyLink>
            </li>
            <li class="HeaderMenuListItem_FlyoutListItem">
              <SkyLink
                class="HeaderMenuListItem_FlyoutListItem_Link"
                :to="expandedChild.href"
                data-test-id="flyout-link"
                secondary
              >
                Shop All {{ expandedChild.text }}
                <template #icon>
                  <SkyIcon
                    class="SkyLink_Icon"
                    name="arrow"
                    aria-hidden="true"
                    secondary
                  />
                </template>
              </SkyLink>
            </li>
          </ul>
        </div>
      </div>
    </template>
  </li>
</template>

<style lang="scss">
.HeaderMenuListItem {
  list-style: none;
  padding-top: var(--spacing-3x);
  padding-bottom: var(--spacing-2x);
  padding-left: var(--spacing-2x);
  display: flex;
  height: 100%;
  white-space: nowrap;
  border-bottom: solid 2px transparent;
  transition: border-color 0.3s ease-out;
  transition-duration: 0.3s, 0.3s, 0.3s;
  transition-timing-function: ease-out, ease-out, ease-out;
  transition-delay: 0s, 0s, 0s;
  transition-property: border-color;

  &_SpecialTreatment {
    background: rgb(0, 129, 129, 0.1);
  }

  > .SkyLink,
  > .SkyButton {
    border-style: none;
  }

  &:hover,
  &:focus-within {
    border-bottom: solid 2px var(--text-color-action-secondary);
  }

  &_Flyout {
    @include container-large;

    position: absolute;
    left: 0;
    top: 100%;
    width: 100%;

    background-color: var(--surface-color-default);
    z-index: var(--z-index-dropdown);
    box-shadow: var(--shadow-default-bottom);
    border: 1px var(--border-color-default);
    border-style: solid none;
  }

  &_FlyoutContainer {
    &--nested {
      display: grid;
      grid-template-columns: minmax(25%, max-content) 1fr;
      gap: var(--spacing-3x);
    }

    // stylelint-disable
    ul:first-of-type {
      font-size: var(--font-size-300);
      background-color: var(--surface-color-frost);

      .SkyLink {
        &:hover,
        &:focus {
          // Maintain 3:1 contrast against --surface-color-frost
          color: var(--text-color-action-primary);
          border-color: transparent;
        }
      }

      .HeaderMenuListItem_FlyoutListItem_Link.SkyLink--secondary {
        &:hover,
        &:focus {
          border-bottom: solid 2px var(--text-color-action-primary);
        }
      }
    }
    // stylelint-enable
  }

  &_FlyoutList {
    margin: 0;
    padding: var(--spacing-3x);

    display: grid;
    grid-template: repeat(7, 1fr) / repeat(2, max-content);
    grid-auto-flow: column;
    gap: var(--spacing-3x) var(--spacing-8x);
  }

  // stylelint-disable
  &_FlyoutContainer {
    ul:first-of-type {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-1x);
    }
  }
  // stylelint-enable

  &_FlyoutListItem {
    list-style: none;
    padding: 0;

    &:last-child {
      grid-column: 1;
      grid-row: -1;
      margin-top: var(--spacing-3x);
      font-size: var(--font-size-100);
      flex-grow: 1;
      display: flex;
      align-items: flex-end;
    }
  }

  &_FlyoutListItemButton {
    text-align: left;
    display: grid;
    grid-template-columns: 1fr auto;
  }

  &_Link {
    display: flex;
    align-items: center;

    &--nested {
      &:hover,
      &:focus {
        &.SkyLink {
          border-color: transparent;
        }
      }
    }
  }
}
</style>
